/*!
 * SPDX-FileCopyrightText: 2025 Bundesrepublik Deutschland, vertreten durch das BMI/ITZBund
 *
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

/*
 * This file is part of the package itzbund/gsb-places of the GSB 11 Project by ITZBund
 *
 * Copyright (C) 2025 Bundesrepublik Deutschland, vertreten durch das
 * BMI/ITZBund. Author: Thomas Maroschik
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

@import "maplibre-gl/dist/maplibre-gl.css";
@import "@deck.gl/widgets/stylesheet.css";

:host {
    position: relative;
    display: block;
    height: 100%;

    --gsb-map-control-focus-ring-color: var(--bs-secondary);
    --gsb-map-canvas-focus-ring-color: var(--gsb-map-control-focus-ring-color);
    --gsb-map-popup-width: 320px;
}

@media screen and (width <= 479px) {
    :host {
        --gsb-map-popup-width: min(60vw, 240px);
    }
}

#map {
    width: 100%;
    height: 100%;
}

slot:not([name]) {
    display: none;
}

#map.maplibregl-map {
    overflow: initial;
}

#map .maplibregl-canvas-container {
    overflow: hidden;
    position: relative;
    height: 100%;
}

#map .maplibregl-canvas-container:has(.maplibregl-canvas:focus) {
    outline: 0.125rem solid var(--gsb-map-canvas-focus-ring-color) !important;
    outline-offset: 0.0625rem;
}

#map .maplibregl-control-container {
    display: var(--gsb-map-control-container-display, inherit);
}

#map .maplibregl-ctrl-top-left {
    display: var(--gsb-map-ctrl-top-left-display, inherit);
}

#map .maplibregl-ctrl-top-right {
    display: var(--gsb-map-ctrl-top-right-display, inherit);
}

#map .maplibregl-ctrl-bottom-left {
    display: var(--gsb-map-ctrl-bottom-left-display, inherit);
}

#map .maplibregl-ctrl-bottom-right {
    display: var(--gsb-map-ctrl-bottom-right-display, inherit);
}

#map .maplibregl-ctrl-attrib-button:focus,
#map .maplibregl-ctrl-group button:focus {
    box-shadow: none;
    outline: 0.125rem solid var(--gsb-map-control-focus-ring-color) !important;
}

#map .maplibregl-marker {
    --gsb-map-marker-color: var(--bs-primary);
    z-index: 1;
}

#map .maplibregl-marker[tabindex]:active, #map .maplibregl-marker[tabindex]:focus, #map .maplibregl-marker[tabindex]:hover {
    --gsb-map-marker-color: var(--bs-secondary);
    cursor: pointer;
}

#map .maplibregl-marker[tabindex]:focus {
    outline: 0.125rem solid var(--gsb-map-control-focus-ring-color) !important;
    outline-offset: 0.0625rem;
}

#map .maplibregl-popup {
    z-index: 1000;
}

#map .maplibregl-popup-close-button {
    font-size: var(--gsb-map-close-button-size, 2rem);
    position: static;
    float: right;
    margin: -10px -10px 0 0;
}

#map .maplibregl-popup-close-button:focus {
    outline: 0.125rem solid var(--gsb-map-control-focus-ring-color) !important;
}

#map .maplibregl-popup-content {
    box-shadow: 0 0 0 2px var(--bs-emphasis-color);
    border-radius: 0;
    padding: 10px;
}

#map .maplibregl-popup-tip {
    width: 0;
    height: 0;
    position: relative;
}

/* =========================
   BOTTOM
   ========================= */
#map .maplibregl-popup-anchor-bottom .maplibregl-popup-tip {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid var(--bs-emphasis-color);
}

#map .maplibregl-popup-anchor-bottom .maplibregl-popup-tip::after {
    content: "";
    position: absolute;
    left: -8px;
    top: -10px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #fff;
}

/* =========================
   TOP
   ========================= */
#map .maplibregl-popup-anchor-top .maplibregl-popup-tip {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid var(--bs-emphasis-color);
}

#map .maplibregl-popup-anchor-top .maplibregl-popup-tip::after {
    content: "";
    position: absolute;
    left: -8px;
    top: 2px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #fff;
}

/* =========================
   LEFT
   ========================= */
#map .maplibregl-popup-anchor-left .maplibregl-popup-tip {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid var(--bs-emphasis-color);
}

#map .maplibregl-popup-anchor-left .maplibregl-popup-tip::after {
    content: "";
    position: absolute;
    top: -8px;
    left: 2px;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #fff;
}

/* =========================
   RIGHT
   ========================= */
#map .maplibregl-popup-anchor-right .maplibregl-popup-tip {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 10px solid var(--bs-emphasis-color);
}

#map .maplibregl-popup-anchor-right .maplibregl-popup-tip::after {
    content: "";
    position: absolute;
    top: -8px;
    left: -10px;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid #fff;
}
